/**
 * Landing sections 6–10 (how it works → capabilities → CLI → security → final CTA).
 * Layouts mirror plane.so (docs/landing/RISET_PLANE_SO.md §2 rows 6–8, 11–13); all copy via Paraglide.
 */
import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  Check,
  ClipboardList,
  FileCheck2,
  FolderGit2,
  KeyRound,
  ScrollText,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'
import { Mascot } from '#/components/layout/mascot'
import { Container, FeatureCol, MkLink, Overline, SectionHead } from './mk'

// ---------------------------------------------------------------- 6. how it works (plane.so "migration" timeline)

function StepCol({
  tag,
  title,
  items,
  first,
}: {
  tag: string
  title: string
  items: string[]
  first?: boolean
}) {
  return (
    <div
      className={cn(
        'px-6 pt-8 pb-10',
        !first &&
          'border-t border-[var(--mk-border)] lg:border-t-0 lg:border-l',
      )}
    >
      <Overline className="text-[13px]">{tag}</Overline>
      <h3 className="mt-3 text-xl">{title}</h3>
      <ul className="mt-10 flex flex-col gap-3">
        {items.map((t) => (
          <li
            key={t}
            className="flex items-start gap-3 text-[15px] leading-relaxed text-[var(--mk-fg)]"
          >
            <Check
              className="mt-1 size-4 shrink-0 text-[var(--mk-fg-muted)]"
              aria-hidden
            />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function HowItWorks() {
  const steps = [
    {
      tag: m.mk_how_1_tag(),
      title: m.mk_how_1_title(),
      items: [m.mk_how_1_a(), m.mk_how_1_c()],
    },
    {
      tag: m.mk_how_2_tag(),
      title: m.mk_how_2_title(),
      items: [m.mk_how_2_a(), m.mk_how_2_c()],
    },
    {
      tag: m.mk_how_3_tag(),
      title: m.mk_how_3_title(),
      items: [m.mk_how_3_a(), m.mk_how_3_b(), m.mk_how_3_c()],
    },
    {
      tag: m.mk_how_4_tag(),
      title: m.mk_how_4_title(),
      items: [m.mk_how_4_a(), m.mk_how_4_c()],
    },
  ]
  return (
    <section id="how-it-works" className="scroll-mt-20 py-24 lg:py-32">
      <Container>
        <SectionHead
          overline={m.mk_how_overline()}
          title={m.mk_how_title()}
          lead={m.mk_how_sub()}
        />
        <div className="mt-16 lg:mt-20">
          <div className="mk-ruler" aria-hidden />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <StepCol key={s.tag} first={i === 0} {...s} />
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 8. CLI & agent-native (dark, terminal mock)

/**
 * CSS-only "typing" reveal for the terminal lines, driven by scroll (no timers, no effects): each line pops in as the
 * frame enters the viewport, staggered by `--i`. The section sits far below the fold, so a time-based reveal would
 * have finished long before anyone sees it. The named view timeline lives on the frame (`data-term-scope`) rather
 * than the terminal because `.mk-frame` has `overflow: hidden`, which counts as a scroll container for `view()`.
 * Browsers without scroll-driven animations (or reduced motion) simply show every line.
 */
const TERM_CSS = `
@keyframes mk-term-in{to{opacity:1}}
@supports (animation-timeline: view()){
.mk [data-term-scope]{view-timeline-name:--mk-term;view-timeline-axis:block}
.mk .mk-terminal [data-line]{opacity:0;animation:mk-term-in steps(1,end) both;animation-timeline:--mk-term;animation-range:entry calc(6% + var(--i) * 6%) entry calc(8% + var(--i) * 6%)}
@media (prefers-reduced-motion:reduce){.mk .mk-terminal [data-line]{opacity:1;animation:none}}
}
`

type TermLine = { kind: 'cmd' | 'ok' | 'err' | 'arrow' | 'dim'; text: string }

function Terminal() {
  // Existing single-task commands remain available in the downloadable CLI.
  const lines: TermLine[] = [
    { kind: 'cmd', text: 'bystep task next' },
    { kind: 'arrow', text: m.mk_term_l1() },
    { kind: 'dim', text: m.mk_term_l2() },
    { kind: 'cmd', text: 'bystep task verify F1-T3' },
    { kind: 'err', text: m.mk_term_l3() },
    { kind: 'ok', text: m.mk_term_l4() },
    { kind: 'dim', text: m.mk_term_l5() },
    { kind: 'dim', text: m.mk_term_l6() },
    { kind: 'cmd', text: 'bystep task complete F1-T3' },
    { kind: 'ok', text: m.mk_term_l7() },
    { kind: 'ok', text: m.mk_term_l8() },
    { kind: 'ok', text: m.mk_term_l9() },
    { kind: 'dim', text: m.mk_term_l10() },
  ]
  return (
    <div
      className="mk-terminal mx-auto -mb-6 max-h-[480px] w-full max-w-3xl overflow-hidden"
      role="img"
      aria-label={m.mk_term_title()}
    >
      <style href="mk-term-reveal" precedence="default">
        {TERM_CSS}
      </style>
      <div className="flex items-center gap-3 border-b border-[var(--mk-dark-border)] px-4 py-2.5">
        <div className="flex gap-1.5" aria-hidden>
          <span className="size-2.5 rounded-full bg-[#ff5f57]" />
          <span className="size-2.5 rounded-full bg-[#febc2e]" />
          <span className="size-2.5 rounded-full bg-[#28c840]" />
        </div>
        <span className="dim text-xs">{m.mk_term_title()}</span>
      </div>
      <div className="px-5 py-4 whitespace-pre-wrap break-words">
        {lines.map((l, i) => (
          <div
            key={i}
            data-line
            className={l.kind}
            style={{ '--i': i } as React.CSSProperties}
          >
            {l.kind === 'cmd' ? <span className="dim">$ </span> : null}
            {l.text}
          </div>
        ))}
        <div
          data-line
          className="cmd"
          style={{ '--i': lines.length } as React.CSSProperties}
        >
          <span className="dim">$ </span>
          <span className="cursor" aria-hidden />
        </div>
      </div>
    </div>
  )
}

export function CliSection() {
  return (
    <section id="cli" className="mk-dark-section scroll-mt-20 py-24 lg:py-32">
      <Container>
        <SectionHead
          align="left"
          overline={m.mk_cli_overline()}
          title={m.mk_cli_title()}
          lead={m.mk_cli_sub()}
          actions={
            <>
              <MkLink to="/docs">{m.mk_cli_cta_primary()}</MkLink>
              <MkLink to="/serve" variant="secondary" arrow>
                {m.mk_desktop_guide()}
              </MkLink>
            </>
          }
        />
        <div
          data-term-scope
          className="mk-frame mk-frame--dark mt-12 px-6 pt-10 pb-0 sm:px-16 sm:pt-14"
        >
          <p className="relative mb-4 text-xs text-[var(--mk-dark-fg-muted)]">
            {m.mk_mock_label()}
          </p>
          <Terminal />
        </div>
        <div className="grid gap-12 pt-14 md:grid-cols-3">
          <FeatureCol title={m.mk_cli_1_title()}>
            {m.mk_cli_1_desc()}
          </FeatureCol>
          <FeatureCol title={m.mk_cli_2_title()}>
            {m.mk_cli_2_desc()}
          </FeatureCol>
          <FeatureCol title={m.mk_cli_3_title()}>
            {m.mk_cli_3_desc()}
          </FeatureCol>
        </div>
        <p className="mt-12 inline-flex items-center gap-2 rounded-full border border-[var(--mk-dark-border)] px-4 py-2 text-sm text-[var(--mk-dark-fg-muted)]">
          <ClipboardList className="size-4" aria-hidden />
          {m.mk_cli_oss()}
        </p>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 9. security (three cards with → links)

function SecCard({
  icon: Icon,
  title,
  desc,
  link,
  to,
}: {
  icon: LucideIcon
  title: string
  desc: string
  link: string
  to: '/docs' | '/settings'
}) {
  return (
    <div className="mk-card-white flex flex-col p-7">
      <div className="flex size-10 items-center justify-center rounded-lg border border-[var(--mk-border)] bg-[var(--mk-bg-muted)] text-[var(--mk-fg)]">
        <Icon className="size-[18px]" aria-hidden />
      </div>
      <h3 className="mt-10 text-lg">{title}</h3>
      <p className="mk-muted mt-2 leading-relaxed">{desc}</p>
      <Link
        to={to}
        className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-medium text-[var(--mk-fg)] transition-colors hover:text-[var(--mk-fg-muted)]"
      >
        {link}
        <ArrowRight className="size-4" aria-hidden />
      </Link>
    </div>
  )
}

export function Security() {
  return (
    <section id="security" className="py-24 lg:py-32">
      <Container>
        <SectionHead
          align="left"
          overline={m.mk_sec_overline()}
          title={m.mk_sec_title()}
          lead={m.mk_sec_sub()}
        />
        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <SecCard
            icon={FileCheck2}
            title={m.mk_sec_1_title()}
            desc={m.mk_sec_1_desc()}
            link={m.mk_sec_1_link()}
            to="/docs"
          />
          <SecCard
            icon={KeyRound}
            title={m.mk_sec_2_title()}
            desc={m.mk_sec_2_desc()}
            link={m.mk_sec_2_link()}
            to="/settings"
          />
          <SecCard
            icon={FolderGit2}
            title={m.mk_sec_3_title()}
            desc={m.mk_sec_3_desc()}
            link={m.mk_sec_3_link()}
            to="/docs"
          />
          <SecCard
            icon={ScrollText}
            title={m.mk_sec_4_title()}
            desc={m.mk_sec_4_desc()}
            link={m.mk_sec_4_link()}
            to="/docs"
          />
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 10. final CTA on a full-bleed wash

export function FinalCta() {
  return (
    <section>
      {/* `text-white` sits on the wrapper because `.mk h2` uses `color: inherit`; button colours need `!` to beat `.mk .mk-btn-*` */}
      {/* Static backdrop keeps the final CTA readable without downloading an autoplay video. */}
      <div className="relative overflow-hidden py-28 text-white">
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#35466a_0%,#171e2e_45%,#0f1115_85%)]"
          aria-hidden
        />
        <Container className="relative flex flex-col items-center gap-8 text-center">
          <Mascot size={160} />
          <h2 className="max-w-[22ch] drop-shadow-[0_2px_12px_rgba(0,0,0,.35)]">
            {m.mk_final_title()}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            <MkLink
              to="/sign-in"
              search={{ redirect: '/home' }}
              className="bg-white! text-[var(--mk-btn)]! hover:bg-[#e8eaee]!"
            >
              {m.mk_final_cta_primary()}
            </MkLink>
            <MkLink
              to="/consulting"
              variant="secondary"
              arrow
              className="border-transparent! bg-[rgba(15,17,21,.75)]! text-white! hover:bg-[rgba(15,17,21,.9)]!"
            >
              {m.mk_final_cta_secondary()}
            </MkLink>
          </div>
        </Container>
      </div>
    </section>
  )
}
